/* BulkImportDialog — system admin uploads a CSV (email[,name]); backend
   creates each account, issues its Scan2Call QR and emails the poster.
   Also shows the history of past imports (who, when, how many). */
import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileUp, CheckCircle2, AlertTriangle, Download, History, ArrowLeft, ChevronDown, ChevronUp, Mail } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatWhen(iso) {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function HistoryList({ items, loading }) {
  const [openId, setOpenId] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  const resend = async (userId) => {
    setResendingId(userId);
    try {
      const r = await axios.post(`${API}/users/${userId}/resend-qr`, {}, { withCredentials: true });
      toast.success(`QR email re-sent to ${r.data.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not re-send the email");
    } finally {
      setResendingId(null);
    }
  };

  if (loading) return <p className="text-sm text-gray-400 font-body text-center py-6">Loading…</p>;
  if (!items.length) return <p className="text-sm text-gray-400 font-body text-center py-6">No CSV imports yet.</p>;
  return (
    <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="import-history-list">
      {items.map((imp) => {
        const expanded = openId === imp.import_id;
        return (
          <div key={imp.import_id} className="border border-gray-200 rounded-sm" data-testid={`import-history-${imp.import_id}`}>
            <button
              type="button"
              onClick={() => setOpenId(expanded ? null : imp.import_id)}
              className="w-full text-left px-3 py-2 flex items-center justify-between gap-2 hover:bg-gray-50"
              data-testid={`import-history-toggle-${imp.import_id}`}
            >
              <div className="min-w-0">
                <p className="text-xs font-body font-bold text-gray-800 truncate">{imp.filename}</p>
                <p className="text-[10px] text-gray-500 font-body">
                  {formatWhen(imp.created_at)} · by {imp.admin_name || "admin"}
                  {imp.estate ? ` · ${imp.estate}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] font-body font-bold text-green-700 bg-green-50 border border-green-200 rounded-sm px-1.5 py-0.5">
                  {imp.created.length} added
                </span>
                {imp.skipped.length > 0 && (
                  <span className="text-[10px] font-body text-gray-500 bg-gray-50 border border-gray-200 rounded-sm px-1.5 py-0.5">
                    {imp.skipped.length} skipped
                  </span>
                )}
                {expanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
              </div>
            </button>
            {expanded && (
              <div className="px-3 pb-2 border-t border-gray-100 pt-2 space-y-1">
                {imp.created.map((c) => (
                  <div key={c.user_id} className="flex items-center justify-between gap-2">
                    <p className="text-[11px] font-body text-gray-700 min-w-0 truncate">
                      {c.name} — {c.email}{" "}
                      <span className={c.email_sent ? "text-green-600" : "text-amber-600"}>
                        {c.email_sent ? "· QR emailed" : "· email failed"}
                      </span>
                    </p>
                    <button
                      type="button"
                      onClick={() => resend(c.user_id)}
                      disabled={resendingId === c.user_id}
                      className="text-[10px] font-body font-bold text-gray-500 hover:text-black inline-flex items-center gap-1 shrink-0 disabled:opacity-50"
                      data-testid={`resend-qr-${c.user_id}`}
                    >
                      {resendingId === c.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                      Resend
                    </button>
                  </div>
                ))}
                {imp.skipped.map((s, i) => (
                  <p key={i} className="text-[11px] font-body text-gray-400">
                    {s.email} — skipped ({s.reason})
                  </p>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function BulkImportDialog({ open, onClose, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [estate, setEstate] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [view, setView] = useState("upload");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const reset = () => {
    setFile(null);
    setResult(null);
    setView("upload");
    if (fileRef.current) fileRef.current.value = "";
  };

  const openHistory = async () => {
    setView("history");
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/users/bulk-imports`, { withCredentials: true });
      setHistory(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load import history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "email,name,estate\nada.obi@example.com,Ada Obi,Sunrise Gardens\nchidi.eze@example.com,Chidi Eze,Sunrise Gardens\n,,\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "guard-residents-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      if (estate.trim()) form.append("estate", estate.trim());
      const r = await axios.post(`${API}/users/bulk-import`, form, { withCredentials: true });
      setResult(r.data);
      if (r.data.created.length) onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            {view === "history" ? <History className="w-4 h-4" /> : <FileUp className="w-4 h-4" />}
            {view === "history" ? "Past CSV imports" : "Import residents from CSV"}
          </DialogTitle>
          <DialogDescription className="font-body text-xs">
            {view === "history" ? (
              "Every CSV import — who ran it, when, and which residents were added."
            ) : (
              <>
                Upload a CSV with columns <code className="font-bold">email,name,estate</code> (name and
                estate optional, max 50 rows). Every valid row gets an account, a Scan2Call QR, and the
                welcome email with their printable poster.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {view === "history" ? (
          <HistoryList items={history} loading={historyLoading} />
        ) : !result ? (
          <>
            <div
              className="border-2 border-dashed border-gray-300 rounded-sm p-6 text-center cursor-pointer hover:border-gray-400"
              onClick={() => fileRef.current?.click()}
              data-testid="bulk-import-dropzone"
            >
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="bulk-import-file-input"
              />
              <FileUp className="w-6 h-6 mx-auto text-gray-400 mb-2" />
              <p className="text-sm font-body text-gray-700">
                {file ? <span className="font-bold">{file.name}</span> : "Click to choose a CSV file"}
              </p>
              <p className="text-[10px] text-gray-400 font-body mt-1">e.g. resident@estate.com,Ada Obi</p>
            </div>
            <input
              value={estate}
              maxLength={60}
              onChange={(e) => setEstate(e.target.value)}
              placeholder="Estate name for this batch (optional) — e.g. Sunrise Gardens"
              className="w-full border border-gray-300 rounded-sm h-9 px-3 text-sm font-body focus:outline-none focus:border-gray-500"
              data-testid="bulk-import-estate-input"
            />
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={downloadTemplate}
                className="text-xs font-body font-bold text-gray-600 hover:text-black inline-flex items-center gap-1.5"
                data-testid="bulk-import-template-btn"
              >
                <Download className="w-3.5 h-3.5" /> Download sample CSV template
              </button>
              <button
                type="button"
                onClick={openHistory}
                className="text-xs font-body font-bold text-gray-600 hover:text-black inline-flex items-center gap-1.5"
                data-testid="bulk-import-history-btn"
              >
                <History className="w-3.5 h-3.5" /> Past imports
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="bulk-import-results">
            <div className="flex items-center gap-2 text-sm font-body text-green-700 bg-green-50 border border-green-200 rounded-sm px-3 py-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              {result.created.length} account{result.created.length === 1 ? "" : "s"} created —{" "}
              {result.emails_sent} QR email{result.emails_sent === 1 ? "" : "s"} sent
            </div>
            {result.created.some((c) => !c.email_sent) && (
              <div className="text-xs font-body text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                Email failed for:{" "}
                {result.created.filter((c) => !c.email_sent).map((c) => c.email).join(", ")} — share
                their QR manually from their account.
              </div>
            )}
            {result.skipped.length > 0 && (
              <div className="text-xs font-body text-gray-600 bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
                <p className="font-bold mb-1">{result.skipped.length} row{result.skipped.length === 1 ? "" : "s"} skipped:</p>
                <ul className="space-y-0.5">
                  {result.skipped.map((s, i) => (
                    <li key={i}>
                      {s.email} — <span className="text-gray-400">{s.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {view === "history" ? (
            <Button variant="outline" onClick={() => setView("upload")} className="rounded-sm font-body text-xs" data-testid="import-history-back">
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to import
            </Button>
          ) : !result ? (
            <>
              <Button variant="outline" onClick={close} className="rounded-sm font-body text-xs" data-testid="bulk-import-cancel">
                Cancel
              </Button>
              <Button
                onClick={upload}
                disabled={busy || !file}
                className="bg-black hover:bg-gray-800 text-white rounded-sm font-body text-xs"
                data-testid="bulk-import-submit"
              >
                {busy ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Importing &amp; emailing…</>
                ) : (
                  "Import & email QRs"
                )}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={reset} className="rounded-sm font-body text-xs" data-testid="bulk-import-another">
                Import another file
              </Button>
              <Button onClick={close} className="bg-black hover:bg-gray-800 text-white rounded-sm font-body text-xs" data-testid="bulk-import-done">
                Done
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
